/**
 * Step 4 - Service picker.
 *
 * Uses the full branch category tree:
 *   GET /api/{locale}/categories/tree/{branch}
 *
 * The UI mirrors the beauty demo catalog: category chips, search and
 * collapsible groups. Leaf services are selected directly and advance to
 * the time step.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
  fetchCategories,
  getServiceCategoryId,
  getServiceMeta,
  getServicePrice,
  getServiceUserId,
  pickTranslation,
} from "../../helper/bookingApi";
import {
  BOOKING_FONT_FAMILY,
  EmptyState,
  RetryButton,
  SkeletonRow,
  StepCard,
  StepFooter,
  StepHeader,
  STEP_PAD,
} from "./BookingPrimitives";

const INITIAL_VISIBLE_SERVICE_COUNT = 5;
const SERVICE_VISIBLE_INCREMENT = 12;
function getServiceNearestTimes(item) {
  const source = [
    item?.time_suggestions?.times,
    item?.nearest_times,
    item?.nearestTimes,
    item?.time_suggestions,
    item?.timeSuggestions,
    item?.available_times,
    item?.availableTimes,
  ].find(Array.isArray);

  const toSlot = (value, index) => {
    const rawValue = String(value || "");
    const time = rawValue.match(/\d{1,2}:\d{2}/)?.[0] || "";
    if (!time) return null;
    const datePart = rawValue.match(/\d{4}-\d{2}-\d{2}/)?.[0];
    const today = new Date();
    const fallbackDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const raw = datePart ? rawValue : `${fallbackDate} ${time}:00`;
    const date = new Date(raw.replace(" ", "T"));
    if (Number.isNaN(date.getTime())) return null;
    return { key: `${raw}-${index}`, time, raw, date };
  };

  if (!source?.length) return [];

  const times = source
    .map((slot, index) => {
      const value =
        typeof slot === "string"
          ? slot
          : slot?.time || slot?.label || slot?.datetime || slot?.date_time || "";
      return toSlot(value, index);
    })
    .filter(Boolean);

  return times;
}

function groupServiceTimes(slots, now = new Date()) {
  const dateKey = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const groups = new Map();
  for (const slot of [...slots].sort((a, b) => a.date - b.date)) {
    const key = dateKey(slot.date);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        labelKey: key === dateKey(now)
          ? "booking.specialists.nearestToday"
          : key === dateKey(tomorrow)
            ? "booking.specialists.nearestTomorrow"
            : null,
        label: `${String(slot.date.getDate()).padStart(2, "0")}.${String(slot.date.getMonth() + 1).padStart(2, "0")}.${slot.date.getFullYear()}:`,
        slots: [],
      });
    }
    groups.get(key).slots.push(slot);
  }
  return [...groups.values()];
}

function formatServicePrice(value) {
  return `${Number(value || 0).toLocaleString("hy-AM")} ֏`;
}

function getChildren(node) {
  const candidates = [
    node?.children,
    node?.categories,
    node?.sub_categories,
    node?.subCategories,
    node?.services,
    node?.items,
  ];
  return candidates.find((item) => Array.isArray(item)) || [];
}

function getNodeType(node) {
  const type = getServiceMeta(node)?.type ?? node?.type;
  const numeric = Number(type);
  return Number.isFinite(numeric) ? numeric : null;
}

function isGroupCategory(category) {
  const type = getNodeType(category);
  if (type === 0) return true;
  if (type === 1) return false;
  return getChildren(category).length > 0;
}

function getServiceDescription(item, lang) {
  if (item?.translate?.description) return item.translate.description;
  if (Array.isArray(item?.translates)) {
    const found = item.translates.find((tr) => tr.description);
    if (found?.description) return found.description;
  }
  return item?.description || item?.category?.description || "";
}

function getDuration(item, lang) {
  const raw =
    item?.average_waiting ??
    item?.duration ??
    item?.waiting_time ??
    item?.category?.average_waiting;
  const minutes = Number(raw);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  if (minutes < 60) {
    return lang === "en" ? `${minutes} min` : `${minutes} ր`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (!rest) return lang === "en" ? `${hours} h` : `${hours} ժ`;
  return lang === "en" ? `${hours} h ${rest} min` : `${hours} ժ ${rest} ր`;
}

function countWorkers(item, t) {
  const windows = getServiceMeta(item)?.windows || [];
  const count =
    item?.workers_count ??
    item?.masters_count ??
    item?.employees_count ??
    (Array.isArray(item?.workers) ? item.workers.length : null) ??
    windows.length;
  const numeric = Number(count);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  return t("booking.service.workersCount", { count: numeric });
}

function getServiceRatingValue(item) {
  const candidates = [
    item?.rating_count,
    item?.ratingCount,
    item?.rating,
    item?.ratings_count,
    item?.ratingsCount,
    item?.ratings?.value,
    item?.ratings?.count,
  ];

  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value > 0) return Math.min(5, Math.max(0, value));
  }

  return 0;
}

function getServiceReviews(item) {
  const candidates = [
    item?.comments,
    item?.reviews,
    item?.feedbacks,
    item?.ratings?.comments,
    item?.category?.comments,
    item?.category?.reviews,
  ];

  return candidates.find((value) => Array.isArray(value)) || [];
}

function getReviewText(review) {
  return (
    review?.comment ||
    review?.feedback ||
    review?.text ||
    review?.message ||
    review?.description ||
    ""
  );
}

function getReviewDate(review) {
  const raw =
    review?.date ||
    review?.created_at ||
    review?.createdAt ||
    review?.updated_at ||
    review?.updatedAt ||
    "";
  if (!raw) return "";
  const value = String(raw);
  return value.includes("T") ? value.slice(0, 10) : value.split(" ")[0];
}

function getReviewAuthor(review, fallback) {
  return (
    review?.author ||
    review?.client_name ||
    review?.clientName ||
    review?.user?.name ||
    fallback
  );
}

function RatingStars({ value, onChange, disabled = false, size = 28 }) {
  return (
    <span className="booking-service-rating-stars">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onClick={() => onChange?.(star)}
          aria-label={`${star}`}
          className={star <= value ? "is-filled" : ""}
          style={{
            border: "none",
            background: "transparent",
            color: star <= value ? "#ffae00" : "#ffffff",
            cursor: disabled ? "default" : "pointer",
            fontSize: size,
            lineHeight: 1,
            padding: 0,
          }}
        >
          <i className="las la-star" aria-hidden="true" />
        </button>
      ))}
    </span>
  );
}

function sameServiceUserId(first, second) {
  if (first == null || second == null) return true;

  return String(first) === String(second);
}

function getNodeKey(item) {
  return String(getServiceCategoryId(item) ?? item?.id ?? "");
}

function getPathChildren(items, path) {
  if (!path.length) return items;
  return getChildren(path[path.length - 1]);
}

function getPathLabel(path, lang) {
  return path.map((item) => pickTranslation(item, lang)).filter(Boolean).join(" / ");
}

function truncateBreadcrumbLabel(value, maxLength = 20) {
  const text = String(value || "");
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}...`;
}

function SelectionMark({ palette, selected, multiple }) {
  const size = 36;
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: multiple ? 8 : "50%",
        background: selected && multiple ? palette.accent : "transparent",
        color: selected && multiple ? "#061415" : palette.accent,
        border: `1.5px solid ${palette.accent}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        fontSize: multiple ? "1rem" : "0.78rem",
        boxShadow: selected ? "0 0 0 3px rgba(48, 232, 189, 0.12)" : "none",
      }}
      aria-hidden="true"
    >
      {selected ? (
        multiple ? (
          <i className="las la-check" />
        ) : (
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              background: palette.accent,
              display: "block",
            }}
          />
        )
      ) : null}
    </span>
  );
}

function ServiceRow({
  palette,
  item,
  lang,
  selected,
  multiple,
  showTimes = false,
  timesDisabled = false,
  disabled,
  hasChildren = false,
  childCount = 0,
  onSelect,
  onIncompatibleInfo,
  onFeedback,
}) {
  const { t } = useTranslation();
  const name = pickTranslation(item, lang);
  const description = getServiceDescription(item, lang);
  const price = getServicePrice(item);
  const duration = getDuration(item, lang);
  const workers = countWorkers(item, t);
  const leafCount = hasChildren ? childCount || countLeafServices(getChildren(item)) : 0;
  const interactive = !disabled;
  const showNearestTimes = showTimes && !hasChildren;
  const nearestTimes = showNearestTimes ? getServiceNearestTimes(item) : [];
  const nearestTimeGroups = groupServiceTimes(nearestTimes);

  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (!interactive) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={interactive ? 0 : -1}
      aria-disabled={disabled || undefined}
      onClick={interactive ? () => onSelect() : undefined}
      onKeyDown={handleKeyDown}
      className="text-start w-100"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontFamily: BOOKING_FONT_FAMILY,
        borderRadius: 14,
        border: selected
          ? `1px solid ${palette.serviceSelectedBorder || palette.accent}`
          : disabled
            ? "1px solid rgba(148, 163, 184, 0.35)"
            : palette.border,
        background: selected
          ? palette.serviceSelectedBg || palette.accentSoft
          : disabled
            ? "rgba(148, 163, 184, 0.08)"
            : palette.serviceBg || palette.surface,
        color: disabled ? palette.textMuted : palette.text,
        padding: "clamp(12px, 3vw, 16px) clamp(12px, 3vw, 18px)",
        cursor: interactive ? "pointer" : "not-allowed",
        opacity: disabled ? 0.72 : 1,
        transition: "background 0.15s ease, border-color 0.15s ease",
        minHeight: 80,
      }}
    >
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          className="d-block"
          style={{
            fontFamily: BOOKING_FONT_FAMILY,
            fontWeight: 700,
            color: disabled ? palette.textMuted : palette.text,
            fontSize: "clamp(0.95rem, 2.4vw, 1.05rem)",
            lineHeight: 1.3,
            wordBreak: "break-word",
          }}
        >
          {name}
        </span>
        {description ? (
          <span
            className="d-block"
            style={{
              color: palette.textMuted,
              fontSize: "0.85rem",
              lineHeight: 1.35,
              marginTop: 4,
              wordBreak: "break-word",
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {description}
          </span>
        ) : null}
        <span
          className="d-flex flex-wrap align-items-center gap-2"
          style={{ marginTop: 8 }}
        >
          {hasChildren ? (
            <span
              style={{
                color: palette.textMuted,
                fontSize: "0.85rem",
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              {t("booking.service.servicesCount", { count: leafCount })}
            </span>
          ) : null}
          {price !== 0 ? (

            <span
              style={{
                color: palette.text,
                fontWeight: 700,
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              {formatServicePrice(price)}
            </span>
          ) : null}
          {duration ? (
            <span
              style={{
                color: palette.textMuted,
                fontSize: "0.85rem",
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              <i className="las la-clock me-1" aria-hidden="true" />
              {duration}
            </span>
          ) : null}
          {workers ? (
            <span
              style={{
                color: palette.textMuted,
                fontSize: "0.85rem",
                fontFamily: BOOKING_FONT_FAMILY,
              }}
            >
              <i className="las la-user-check me-1" aria-hidden="true" />
              {workers}
            </span>
          ) : null}
        </span>
        {showNearestTimes && nearestTimes.length > 0 ? (
          <span
            style={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 10,
            }}
          >
            <span
              style={{
                color: palette.textMuted,
                fontSize: 12,
                lineHeight: "18px",
                marginRight: 4,
              }}
            >
              {t("booking.specialists.nearestTime")}
            </span>
            {nearestTimeGroups.map((group) => (
              <span
                key={group.key}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 4,
                  padding: "3px 5px",
                  borderRadius: 8,
                  background: palette.serviceTimeGroupBg || palette.surfaceAlt,
                  color: palette.textMuted,
                  fontSize: 12,
                }}
              >
                <span style={{ whiteSpace: "nowrap", marginRight: 2 }}>
                  {group.labelKey ? t(group.labelKey) : group.label}
                </span>
                {group.slots.map((slot, index) => (
                  <button
                    className="service-time-btn"
                    key={slot.key || `${slot.time}-${index}`}
                    type="button"
                    aria-disabled={disabled || timesDisabled || undefined}
                    tabIndex={disabled || timesDisabled ? -1 : undefined}
                    title={slot.raw}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (disabled || timesDisabled) return;
                      onSelect(slot);
                    }}
                    style={{
                      minWidth: 54,
                      cursor: disabled || timesDisabled ? "not-allowed" : "pointer",
                      minHeight: 28,
                      padding: "3px 8px",
                      border: palette.serviceTimeBorder
                        ? `1px solid ${palette.serviceTimeBorder}`
                        : palette.borderStrong || palette.border,
                      borderRadius: 5,
                      background: palette.serviceTimeBg || palette.surfaceAlt,
                      color: palette.textMuted,
                      fontSize: 14,
                      lineHeight: "20px",
                    }}
                  >
                    {slot.time}
                  </button>
                ))}
              </span>
            ))}
          </span>
        ) : null}
      </span>
      {hasChildren ? (
        <span
          style={{
            width: 24,
            height: 24,
            color: palette.textMuted,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "1.2rem",
          }}
          aria-hidden="true"
        >
          <i className="las la-angle-right" />
        </span>
      ) : disabled ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onIncompatibleInfo?.(item);
          }}
          aria-label={t("booking.service.incompatibleMessage")}
          style={{
            width: 42,
            height: 42,
            borderRadius: "50%",
            background: palette.surface,
            color: palette.accent,
            border: `1.5px solid ${palette.accent}`,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: "1.25rem",
            cursor: "pointer",
          }}
        >
          <i className="las la-info" aria-hidden="true" />
        </button>
      ) : (
        <SelectionMark palette={palette} selected={selected} multiple={multiple} />
      )}
    </div>
  );
}

function asSelectedServices(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
}

function getSelectedCategoryIds(value) {
  return new Set(
    asSelectedServices(value)
      .map((item) => item?.categoryId ?? getServiceCategoryId(item))
      .filter((id) => id != null),
  );
}

function getSelectedServiceUserId(value) {
  const selected = asSelectedServices(value);

  if (!selected.length) return null;

  return getServiceUserId(selected[0]);
}
// eslint-disable-next-line no-unused-vars
function CategorySection({
  palette,
  category,
  lang,
  childrenNodes,
  openByDefault,
  selectedService,
  selectedUserId,
  allowMultiple,
  onSelect,
  onIncompatibleInfo,
  onFeedback,
  depth = 0,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(Boolean(openByDefault));
  const [visibleServiceLimit, setVisibleServiceLimit] = useState(
    INITIAL_VISIBLE_SERVICE_COUNT,
  );
  const selectedCategoryIds = getSelectedCategoryIds(selectedService);
  const leafCount = countLeafServices(childrenNodes);
  const selectedCount = countSelectedServices(childrenNodes, selectedCategoryIds);
  const visibleChildrenNodes = useMemo(
    () => limitDirectLeafServices(childrenNodes, visibleServiceLimit),
    [childrenNodes, visibleServiceLimit],
  );
  const displayedServiceCount = countDirectLeafServices(visibleChildrenNodes);
  const directServiceCount = countDirectLeafServices(childrenNodes);
  const remainingServiceCount = Math.max(
    directServiceCount - displayedServiceCount,
    0,
  );

  useEffect(() => {
    setOpen(Boolean(openByDefault));
  }, [openByDefault]);

  useEffect(() => {
    setVisibleServiceLimit(INITIAL_VISIBLE_SERVICE_COUNT);
  }, [childrenNodes]);

  return (
    <section
      style={{
        background: palette.surface,
        border: palette.border,
        borderRadius: 16,
        overflow: "hidden",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="w-100 text-start d-flex align-items-center gap-3"
        style={{
          background: "transparent",
          border: "none",
          padding: "clamp(14px, 3vw, 18px)",
          color: palette.text,
          cursor: "pointer",
          fontFamily: BOOKING_FONT_FAMILY,
        }}
        aria-expanded={open}
      >
        <span
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: palette.accentSoft,
            color: palette.accent,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.3rem",
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <i className="las la-layer-group" />
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            className="d-block"
            style={{
              color: palette.text,
              fontFamily: BOOKING_FONT_FAMILY,
              fontWeight: 700,
              fontSize: depth > 0 ? "clamp(0.95rem, 2.4vw, 1.06rem)" : "clamp(1rem, 2.6vw, 1.15rem)",
              lineHeight: 1.3,
              wordBreak: "break-word",
            }}
          >
            {pickTranslation(category, lang)}
          </span>
          <span
            style={{
              color: palette.textMuted,
              fontSize: "0.85rem",
              fontFamily: BOOKING_FONT_FAMILY,
            }}
          >
            {getServiceDescription(category, lang) ||
              t("booking.service.servicesCount", { count: leafCount })}
            {selectedCount ? (
              <span
                style={{
                  color: palette.accent,
                  fontWeight: 700,
                  fontFamily: BOOKING_FONT_FAMILY,
                }}
              >
                {" · "}
                {t("booking.common.selected")}
              </span>
            ) : null}
          </span>
        </span>
        <i
          className="las la-angle-down"
          style={{
            color: palette.textMuted,
            fontSize: "1.2rem",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
          aria-hidden="true"
        />
      </button>

      {open ? (
        <div
          style={{
            display: "grid",
            gap: 8,
            padding: "0 clamp(10px, 2.5vw, 14px) clamp(12px, 2.5vw, 16px)",
          }}
        >
          {visibleChildrenNodes.map((item) => (
            <ServiceNode
              key={`${item.id}-${getServiceCategoryId(item) || "group"}`}
              palette={palette}
              item={item}
              lang={lang}
              selectedService={selectedService}
              selectedUserId={selectedUserId}
              allowMultiple={allowMultiple}
              onSelect={onSelect}
              onIncompatibleInfo={onIncompatibleInfo}
              onFeedback={onFeedback}
              openByDefault={
                Boolean(openByDefault) ||
                containsSelectedService(getChildren(item), selectedCategoryIds)
              }
              depth={depth + 1}
            />
          ))}
          {remainingServiceCount ? (
            <div className="d-flex justify-content-center">
              <button
                type="button"
                className="pill fw-700"
                onClick={() =>
                  setVisibleServiceLimit(
                    (count) => count + SERVICE_VISIBLE_INCREMENT,
                  )
                }
                style={{
                  background: palette.accent,
                  border: "none",
                  color: "#061415",
                  fontFamily: BOOKING_FONT_FAMILY,
                  padding: "10px 18px",
                }}
              >
                {t("booking.service.showMore", {
                  count: Math.min(
                    SERVICE_VISIBLE_INCREMENT,
                    remainingServiceCount,
                  ),
                })}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

function countDirectLeafServices(nodes) {
  return nodes.reduce((sum, item) => sum + (isGroupCategory(item) ? 0 : 1), 0);
}

function limitDirectLeafServices(nodes, limit) {
  let shownServices = 0;

  return nodes.filter((item) => {
    if (isGroupCategory(item)) return true;
    if (shownServices >= limit) return false;
    shownServices += 1;
    return true;
  });
}

function countLeafServices(nodes) {
  return nodes.reduce((sum, item) => {
    if (!isGroupCategory(item)) return sum + 1;
    return sum + countLeafServices(getChildren(item));
  }, 0);
}

function countSelectedServices(nodes, selectedCategoryIds) {
  if (!selectedCategoryIds?.size) return 0;
  return nodes.reduce((sum, item) => {
    if (!isGroupCategory(item)) {
      return sum + (selectedCategoryIds.has(getServiceCategoryId(item)) ? 1 : 0);
    }
    return sum + countSelectedServices(getChildren(item), selectedCategoryIds);
  }, 0);
}

function containsSelectedService(nodes, selectedCategoryIds) {
  return countSelectedServices(nodes, selectedCategoryIds) > 0;
}

function matchesSearch(item, lang, query) {
  if (!query) return true;
  const name = pickTranslation(item, lang).toLowerCase();
  const description = getServiceDescription(item, lang).toLowerCase();
  return name.includes(query) || description.includes(query);
}

function filterTree(nodes, lang, search) {
  const q = search.trim().toLowerCase();
  if (!q) return nodes;
  return nodes
    .map((item) => {
      if (!isGroupCategory(item)) return matchesSearch(item, lang, q) ? item : null;
      const children = filterTree(getChildren(item), lang, search);
      if (children.length > 0 || matchesSearch(item, lang, q)) {
        return { ...item, children };
      }
      return null;
    })
    .filter(Boolean);
}

function ServiceNode({
  palette,
  item,
  lang,
  selectedService,
  selectedUserId,
  allowMultiple,
  showTimes,
  onSelect,
  onDrillDown,
  onIncompatibleInfo,
  onFeedback,
}) {
  const children = getChildren(item);
  if (isGroupCategory(item)) {
    return (
      <ServiceRow
        palette={palette}
        item={item}
        lang={lang}
        selected={false}
        multiple={allowMultiple}
        showTimes={showTimes}
        hasChildren
        childCount={countLeafServices(children)}
        onSelect={() => onDrillDown(item)}
        onIncompatibleInfo={onIncompatibleInfo}
        onFeedback={onFeedback}
      />
    );
  }

  const categoryId = getServiceCategoryId(item);
  const selectedCategoryIds = getSelectedCategoryIds(selectedService);

  const selected =
    categoryId != null && selectedCategoryIds.has(categoryId);

  const serviceUserId = getServiceUserId(item);

  const hasSelectedService =
    asSelectedServices(selectedService).length > 0;

  const disabled =
    allowMultiple &&
    hasSelectedService &&
    !selected &&
    serviceUserId != null &&
    selectedUserId != null &&
    !sameServiceUserId(serviceUserId, selectedUserId);

  return (
    <ServiceRow
      palette={palette}
      item={item}
      lang={lang}
      selected={selected}
      multiple={allowMultiple}
      showTimes={showTimes}
      disabled={disabled}
      timesDisabled={asSelectedServices(selectedService).length > 1}
      onSelect={(preselectedSlot) => onSelect(item, preselectedSlot)}
      onIncompatibleInfo={onIncompatibleInfo}
      onFeedback={onFeedback}
    />
  );
}

function IncompatibleServiceModal({ palette, onClose }) {
  const { t } = useTranslation();

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const modal = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="incompatible-service-title"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "rgba(5, 13, 15, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 18,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          position: "relative",
          width: "min(420px, 100%)",
          background: palette.surface,
          border: palette.border,
          borderRadius: 18,
          boxShadow: palette.shadow,
          padding: "22px 22px 18px",
          color: palette.text,
          fontFamily: BOOKING_FONT_FAMILY,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close", "Close")}
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            width: 34,
            height: 34,
            borderRadius: "50%",
            border: "1px solid rgba(148, 163, 184, 0.35)",
            background: palette.surface,
            color: palette.textMuted,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "1.1rem",
            lineHeight: 1,
          }}
        >
          <i className="las la-times" aria-hidden="true" />
        </button>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            background: palette.accentSoft,
            color: palette.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.45rem",
            margin: "0 auto 12px",
          }}
        >
          <i className="las la-info" aria-hidden="true" />
        </div>
        <h4
          id="incompatible-service-title"
          style={{
            color: palette.text,
            fontFamily: BOOKING_FONT_FAMILY,
            fontSize: "1.05rem",
            fontWeight: 700,
            lineHeight: 1.35,
            margin: "0 0 8px",
          }}
        >
          {t("booking.service.incompatibleMessage")}
        </h4>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

function ServiceFeedbackPanel({
  palette,
  lang,
  service,
  orderTicketId,
  isAuthenticated,
  submitting,
  error,
  success,
  onSubmit,
  onLoginRequired,
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const name = pickTranslation(service, lang);
  const description = getServiceDescription(service, lang);
  const reviews = getServiceReviews(service);
  const canSubmit = Boolean(rating && !submitting && (isAuthenticated ? orderTicketId : true));
  const handleSubmit = () => {
    if (!isAuthenticated) {
      onLoginRequired?.();
      return;
    }
    onSubmit?.({ rating, comment });
  };

  return (
    <div
      style={{
        padding: `14px ${STEP_PAD.x} ${STEP_PAD.contentBottom}`,
        display: "grid",
        gap: 14,
      }}
    >
      <div className="booking-service-feedback-head">
        <div className="booking-service-feedback-summary">
          <span className="booking-service-feedback-icon" aria-hidden="true">
            <i className="las la-concierge-bell" />
          </span>
          <div>
            <h3>{name}</h3>
            {description ? <p>{description}</p> : null}
            <RatingStars value={getServiceRatingValue(service)} disabled size={18} />
          </div>
        </div>
        <div className="booking-service-feedback-form">
          <div className="booking-service-feedback-title">
            {t("booking.feedback.title")}
          </div>
          <RatingStars value={rating} onChange={setRating} disabled={submitting} />
          <label className="booking-service-feedback-label">
            {t("booking.feedback.commentLabel")}
          </label>
          <div className="booking-service-feedback-note">
            {t("booking.feedback.afterBooking")}
          </div>
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            disabled={submitting}
            placeholder={t("booking.feedback.commentPlaceholder")}
            rows={3}
            className="booking-service-feedback-textarea"
          />
          {error ? (
            <div className="booking-service-feedback-error">
              {error.message || t("booking.feedback.error")}
            </div>
          ) : null}
          {success ? (
            <div className="booking-service-feedback-success">
              {t("booking.feedback.success")}
            </div>
          ) : null}
          <button
            type="button"
            disabled={!canSubmit}
            onClick={handleSubmit}
            className="booking-service-feedback-submit pill fw-700"
          >
            {submitting ? t("booking.feedback.saving") : t("booking.feedback.submit")}
          </button>
        </div>
      </div>

      <div className="booking-service-review-list">
        {reviews.length === 0 ? (
          <div className="booking-service-review-empty">
            {t("booking.common.noResults")}
          </div>
        ) : (
          reviews.map((review, index) => {
            const reviewRating = getServiceRatingValue(review);
            const text = getReviewText(review);
            return (
              <div key={review?.id || index} className="booking-service-review-item">
                <span className="booking-service-review-avatar" aria-hidden="true">
                  {String(getReviewAuthor(review, "K")).charAt(0).toUpperCase()}
                </span>
                <div className="booking-service-review-copy">
                  <div className="booking-service-review-meta">
                    <strong>{getReviewAuthor(review, name)}</strong>
                    <span>{getReviewDate(review)}</span>
                  </div>
                  <RatingStars value={reviewRating} disabled size={16} />
                  {text ? <p>{text}</p> : null}
                </div>
              </div>
            );
          })
        )}
      </div>

      <style>{`
        .booking-service-feedback-head {
          display: grid;
          grid-template-columns: minmax(190px, 260px) minmax(0, 1fr);
          gap: 20px;
          align-items: start;
        }

        .booking-service-feedback-summary {
          border: ${palette.border};
          border-radius: 12px;
          padding: 18px;
          display: grid;
          justify-items: center;
          gap: 10px;
          text-align: center;
          color: ${palette.text};
        }

        .booking-service-feedback-icon {
          width: 88px;
          height: 88px;
          border-radius: 12px;
          background: ${palette.accent};
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 38px;
        }

        .booking-service-feedback-summary h3,
        .booking-service-feedback-title {
          color: ${palette.text};
          font-family: ${BOOKING_FONT_FAMILY};
          font-size: 16px;
          font-weight: 700;
          line-height: 22px;
          margin: 0;
        }

        .booking-service-feedback-summary p,
        .booking-service-feedback-note,
        .booking-service-review-item p {
          color: ${palette.textMuted};
          font-family: ${BOOKING_FONT_FAMILY};
          font-size: 13px;
          line-height: 19px;
          margin: 0;
        }

        .booking-service-feedback-form {
          display: grid;
          gap: 8px;
          justify-items: start;
        }

        .booking-service-rating-stars {
          display: inline-flex;
          gap: 2px;
          line-height: 1;
        }

        .booking-service-feedback-label {
          color: ${palette.text};
          font-family: ${BOOKING_FONT_FAMILY};
          font-size: 13px;
          font-weight: 600;
          margin-top: 2px;
        }

        .booking-service-feedback-textarea {
          width: min(420px, 100%);
          border: ${palette.border};
          border-radius: 8px;
          background: ${palette.surfaceAlt};
          color: ${palette.text};
          font-family: ${BOOKING_FONT_FAMILY};
          font-size: 14px;
          line-height: 20px;
          outline: none;
          padding: 10px 12px;
          resize: vertical;
        }

        .booking-service-feedback-submit {
          min-width: 184px;
          border: none;
          background: ${palette.accent};
          color: #061415;
          padding: 10px 18px;
          opacity: ${canSubmit ? 1 : 0.55};
          cursor: ${canSubmit ? "pointer" : "not-allowed"};
        }

        .booking-service-feedback-error {
          color: ${palette.danger || "#ef4444"};
          font-size: 13px;
        }

        .booking-service-feedback-success {
          color: ${palette.success};
          font-size: 13px;
          font-weight: 700;
        }

        .booking-service-review-list {
          display: grid;
          gap: 10px;
        }

        .booking-service-review-item,
        .booking-service-review-empty {
          border: ${palette.border};
          border-radius: 10px;
          background: ${palette.surface};
          color: ${palette.text};
          display: flex;
          gap: 10px;
          padding: 12px;
        }

        .booking-service-review-empty {
          color: ${palette.textMuted};
          justify-content: center;
        }

        .booking-service-review-avatar {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: ${palette.surfaceAlt};
          color: ${palette.text};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 34px;
          font-weight: 700;
        }

        .booking-service-review-copy {
          display: grid;
          gap: 4px;
          min-width: 0;
        }

        .booking-service-review-meta {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          color: ${palette.textMuted};
          font-size: 12px;
        }

        .booking-service-review-meta strong {
          color: ${palette.text};
        }

        @media (max-width: 575px) {
          .booking-service-feedback-head {
            grid-template-columns: 1fr;
          }

          .booking-service-feedback-summary {
            justify-items: start;
            text-align: left;
            grid-template-columns: 72px minmax(0, 1fr);
          }

          .booking-service-feedback-icon {
            width: 72px;
            height: 72px;
            font-size: 32px;
          }
        }
      `}</style>
    </div>
  );
}

function normalizeBoolean(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function getIndustryName(industry, organization, branch, lang) {
  const candidates = [
    industry,
    organization?.industry,
    organization?.organization_type,
    organization?.organizationType,
    organization?.type,
    branch?.industry,
    branch?.organization_type,
    branch?.organizationType,
    branch?.type,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    const name = pickTranslation(candidate, lang) || candidate?.title || candidate?.label;
    if (name) return name;
  }

  return "";
}

export function ServiceStep({
  palette,
  lang,
  industry,
  organization,
  branch,
  selectedWorker,
  selectedService,
  orderTicketId,
  isAuthenticated,
  loadCategories = fetchCategories,
  autoAdvance = true,
  allowMultiple,
  onSelect,
  onBack,
  onNext,
  onSubmitRating,
  onLoginRequired,
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [showIncompatibleModal, setShowIncompatibleModal] = useState(false);
  const [selectedServiceUserId, setSelectedServiceUserId] = useState(null);
  const [feedbackService, setFeedbackService] = useState(null);
  const [feedbackState, setFeedbackState] = useState("idle");
  const [feedbackError, setFeedbackError] = useState(null);
  const [servicePath, setServicePath] = useState([]);

  const branchId = branch?.id;
  const organizationName = pickTranslation(organization, lang);
  const industryName = getIndustryName(industry, organization, branch, lang);
  const canSelectMultiple =
    allowMultiple != null ? Boolean(allowMultiple) : normalizeBoolean(branch?.multi_booking);
  const selectedServices = asSelectedServices(selectedService);
  const hasSelection = selectedServices.length > 0;
  const selectedUserId =
    selectedServiceUserId ?? getSelectedServiceUserId(selectedService);

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await loadCategories(lang, { branchId });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("[booking] services load failed", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [branchId, lang, loadCategories]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!selectedServices.length) {
      setSelectedServiceUserId(null);
    }
  }, [selectedServices.length]);

  useEffect(() => {
    setServicePath([]);
    setSearch("");
  }, [branchId, items]);

  const currentNodes = useMemo(
    () => getPathChildren(items, servicePath),
    [items, servicePath],
  );
  const visibleNodes = useMemo(() => {
    return filterTree(currentNodes, lang, search);
  }, [currentNodes, lang, search]);
  const breadcrumbLabel = getPathLabel(servicePath, lang);
  const hasNestedPath = servicePath.length > 0;
  const serviceSearch =
    !feedbackService && !loading && !error && items.length > 0 ? (
      <div
        className="booking-service-search-input d-flex align-items-center"
        style={{
          background: palette.surface,
          border: palette.border,
          borderRadius: 14,
          padding: "10px 16px",
          width: "min(480px, 100%)",
          minWidth: 0,
        }}
      >
        <img
          src="/assets/images/icons/search.svg"
          alt=""
          style={{
            width: 20,
            height: 20,
            marginRight: 10,
            flexShrink: 0,
            opacity: 0.72,
          }}
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={lang === "hy" ? "Որոնել " : t("booking.service.searchPlaceholder")}
          style={{
            fontFamily: BOOKING_FONT_FAMILY,
            background: "transparent",
            border: "none",
            outline: "none",
            color: palette.text,
            flex: 1,
            minWidth: 0,
            fontSize: "0.95rem",
          }}
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            style={{
              border: "none",
              background: "transparent",
              color: palette.textMuted,
              fontSize: "1.1rem",
              flexShrink: 0,
            }}
            aria-label={t("booking.service.searchPlaceholder")}
          >
            <i className="las la-times" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    ) : null;

  const handleBackClick = () => {
    if (feedbackService) {
      closeFeedback();
      return;
    }
    if (hasNestedPath) {
      setServicePath((path) => path.slice(0, -1));
      setSearch("");
      return;
    }
    onBack?.();
  };

  const handleBreadcrumbClick = (index) => {
    setServicePath((path) => path.slice(0, index + 1));
    setSearch("");
  };

  const handleDrillDown = (item) => {
    const nodeKey = getNodeKey(item);
    const sourceItem =
      currentNodes.find((node) => getNodeKey(node) === nodeKey) || item;
    setServicePath((path) => [...path, sourceItem]);
    setSearch("");
  };

  const handleSelect = (category, preselectedSlot = null) => {
    const categoryId = getServiceCategoryId(category);
    const categoryUserId = getServiceUserId(category);
    const alreadySelected = selectedServices.some(
      (item) => (item?.categoryId ?? getServiceCategoryId(item)) === categoryId,
    );

    if (
      !alreadySelected &&
      canSelectMultiple &&
      selectedServices.length > 0 &&
      categoryUserId != null &&
      selectedUserId != null &&
      !sameServiceUserId(categoryUserId, selectedUserId)
    ) {
      setShowIncompatibleModal(true);
      return;
    }

    const normalizedCategory = {
      ...category,
      categoryId,
      userId: categoryUserId,
    };

    if (canSelectMultiple) {
      const nextSelected = alreadySelected
        ? preselectedSlot
          ? selectedServices
          : selectedServices.filter(
            (item) =>
              (item?.categoryId ?? getServiceCategoryId(item)) !== categoryId,
          )
        : [...selectedServices, normalizedCategory];
      if (!alreadySelected && selectedServices.length === 0) {
        setSelectedServiceUserId(categoryUserId);
      }
      onSelect(nextSelected, preselectedSlot);
      return;
    }

    setSelectedServiceUserId(categoryUserId);
    onSelect(normalizedCategory, preselectedSlot);
    if (!preselectedSlot && autoAdvance && typeof onNext === "function") onNext();
  };

  const closeFeedback = () => {
    setFeedbackService(null);
    setFeedbackState("idle");
    setFeedbackError(null);
  };

  const handleSubmitFeedback = async ({ rating, comment }) => {
    if (!feedbackService || !onSubmitRating) return;
    setFeedbackState("saving");
    setFeedbackError(null);
    try {
      await onSubmitRating({ service: feedbackService, rating, comment });
      setFeedbackState("success");
    } catch (err) {
      setFeedbackError(err);
      setFeedbackState("error");
    }
  };

  return (
    <StepCard
      palette={palette}
      style={{
        fontFamily: BOOKING_FONT_FAMILY,
        display: "flex",
        flexDirection: "column",
        maxHeight: "min(82dvh, 820px)",
        overflow: "hidden",
      }}
    >
      <div
        className="booking-service-topbar"
        style={{
          padding: `14px ${STEP_PAD.x} 0 ${STEP_PAD.x}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <button
          type="button"
          onClick={handleBackClick}
          className="booking-service-back pill fw-600 d-inline-flex align-items-center"
          style={{
            background: "transparent",
            border: palette.border,
            color: palette.textMuted,
            fontFamily: BOOKING_FONT_FAMILY,
            fontSize: "0.9rem",
            lineHeight: 1,
            minHeight: 40,
            padding: "9px 16px",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          <i className="las la-arrow-left me-2" aria-hidden="true" />
          {t("booking.common.back")}
        </button>
        {industryName || organizationName || breadcrumbLabel ? (
          <div
            className="booking-service-breadcrumb"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              flexWrap: "wrap",
              marginLeft: 12,
              color: palette.textMuted,
              fontFamily: BOOKING_FONT_FAMILY,
              fontSize: "0.9rem",
              lineHeight: 1.4,
              minWidth: 0,
            }}
          >
            {industryName ? <span>{industryName}</span> : null}
            {organizationName ? <span>/ {organizationName}</span> : null}
            {servicePath.map((item, index) => {
              const label = pickTranslation(item, lang);
              return (
                <button
                  key={`${getNodeKey(item)}-${index}`}
                  type="button"
                  onClick={() => handleBreadcrumbClick(index)}
                  title={label}
                  aria-label={label}
                  style={{
                    border: "none",
                    background: "transparent",
                    color:
                      index === servicePath.length - 1
                        ? palette.text
                        : palette.textMuted,
                    fontFamily: BOOKING_FONT_FAMILY,
                    fontSize: "inherit",
                    fontWeight: index === servicePath.length - 1 ? 700 : 500,
                    lineHeight: 1.4,
                    padding: 0,
                    maxWidth: 260,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  / {truncateBreadcrumbLabel(label)}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      {!feedbackService ? (
        <StepHeader
          palette={palette}
          title={t("booking.service.title")}
          subtitle={t("booking.service.subtitle")}
          accessory={serviceSearch}
        />
      ) : null}

      {feedbackService ? (
        <ServiceFeedbackPanel
          palette={palette}
          lang={lang}
          service={feedbackService}
          orderTicketId={orderTicketId}
          isAuthenticated={isAuthenticated}
          submitting={feedbackState === "saving"}
          error={feedbackError}
          success={feedbackState === "success"}
          onSubmit={handleSubmitFeedback}
          onLoginRequired={onLoginRequired}
          onBack={closeFeedback}
        />
      ) : (
        <div
          className="booking-service-scroll"
          style={{
            padding: `4px ${STEP_PAD.x} ${STEP_PAD.contentBottom}`,
            flex: "1 1 auto",
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          {loading ? (
            <SkeletonRow palette={palette} count={5} />
          ) : error ? (
            <EmptyState
              palette={palette}
              icon="las la-exclamation-circle"
              title={t("booking.errors.loadServices")}
              action={<RetryButton palette={palette} onClick={load} />}
            />
          ) : items.length === 0 ? (
            <EmptyState
              palette={palette}
              icon="las la-folder-open"
              title={t("booking.service.emptyChildren")}
            />
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {visibleNodes.length === 0 ? (
                <EmptyState
                  palette={palette}
                  icon="las la-search"
                  title={t("booking.common.noResults")}
                />
              ) : (
                <>
                  <div style={{ display: "grid", gap: 10 }}>
                    {visibleNodes.map((item) => (
                      <ServiceNode
                        key={`${item.id}-${getServiceCategoryId(item) || "group"}`}
                        palette={palette}
                        item={item}
                        lang={lang}
                        selectedService={selectedService}
                        selectedUserId={selectedUserId}
                        allowMultiple={canSelectMultiple}
                        showTimes={Boolean(selectedWorker?.id)}
                        onSelect={handleSelect}
                        onDrillDown={handleDrillDown}
                        onIncompatibleInfo={() => setShowIncompatibleModal(true)}
                        onFeedback={(item) => setFeedbackService(item)}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <style>{`
        .booking-service-search-input {
          max-width: 480px;
        }
          .service-time-btn:hover {
  border-color: #00D3A1 !important;
}

        .booking-step-footer:has(.booking-service-confirm) {
          justify-content: center !important;
          background: ${palette.surface} !important;
          border-top: ${palette.border} !important;
          padding: 12px 24px 15px !important;
        }

        .booking-service-scroll {
          scrollbar-width: thin;
          scrollbar-color: ${palette.accent} transparent;
          overscroll-behavior: contain;
        }

        .booking-service-scroll::-webkit-scrollbar {
          width: 7px;
        }

        .booking-service-scroll::-webkit-scrollbar-thumb {
          background: ${palette.accent};
          border-radius: 999px;
        }

        .booking-service-confirm {
          min-width: min(369px, 100%);
          min-height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          font-family: ${BOOKING_FONT_FAMILY};
          font-size: 16px;
          line-height: 24px;
          border-radius: 10px !important;
        }

        .booking-service-back:hover,
        .booking-service-back:focus-visible,
        .booking-service-breadcrumb button:hover,
        .booking-service-breadcrumb button:focus-visible {
          border-color: rgba(48, 232, 189, 0.7) !important;
          color: ${palette.text} !important;
          outline: none;
        }

        @media (max-width: 575px) {
          .booking-service-topbar {
            padding-top: 12px !important;
          }

          .booking-service-back {
            min-height: 40px !important;
            padding: 9px 16px !important;
            font-size: 0.9rem !important;
            white-space: nowrap !important;
            flex-shrink: 0;
          }

          .booking-service-search-input {
            width: 100% !important;
            max-width: 100%;
          }

          .booking-service-confirm {
            min-width: 100%;
          }

        }
      `}</style>

      {!feedbackService && (canSelectMultiple || !autoAdvance) ? (
        <StepFooter palette={palette} sticky>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasSelection}
            className="booking-service-confirm pill fw-700"
            style={{
              background: palette.accent,
              color: "#033529",
              border: "none",
              padding: "10px 24px",
              opacity: hasSelection ? 1 : 0.5,
              cursor: hasSelection ? "pointer" : "not-allowed",
            }}
          >
            {t("booking.feedback.submit")}
            <i className="las la-arrow-right ms-2" aria-hidden="true" />
          </button>
        </StepFooter>
      ) : null}
      {showIncompatibleModal ? (
        <IncompatibleServiceModal
          palette={palette}
          onClose={() => setShowIncompatibleModal(false)}
        />
      ) : null}
    </StepCard>
  );
}
